import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Box,
  Button,
  Card,
  Chip,
  ClickAwayListener,
  Divider,
  Grid,
  IconButton,
  Modal,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import ErrorIconAlert from 'src/icons/untitled-ui/duocolor/month-end-alerts/errorIcon';
import WarningIconAlert from 'src/icons/untitled-ui/duocolor/month-end-alerts/warningIcon';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { permissions } from 'src/paths';
import {
  getDepartmentDashboardCount,
  getDepartmentPto,
  getDepartmentTherapyMin,
  getSupervisoryAlertSettingAction,
  resetDepartmentPTORequest,
} from 'src/redux/actions/departmentDashboard';
import {
  getListOfMonthAlertRequest,
  getMonthEndAlertCount,
} from 'src/redux/actions/monthEndAlertsAction';
import LoaderIcon from '../../../src/components/LoaderImg/loader.gif';
import alertCicleIcon from '../../icons/untitled-ui/duocolor/alertCicleIcon.svg';
import calendarIcon from '../../icons/untitled-ui/duocolor/calendarIcon.svg';
import clipboard from '../../icons/untitled-ui/duocolor/clipboard.svg';
import dashboardIcon from '../../icons/untitled-ui/duocolor/dashboardIcon.svg';
import mailIcon from '../../icons/untitled-ui/duocolor/mailIcon.svg';
import mailIcon1 from '../../icons/untitled-ui/duocolor/mailIcon1.svg';
import monthAlertIcon from '../../icons/untitled-ui/duocolor/monthAlertIcon.svg';
import monthEndPencil from '../../icons/untitled-ui/duocolor/monthEndPencil.svg';
import { RedirectByActionAlert } from '../RedirectByActionAlert';
import TenthSupervisorAlert from './10th-supervisor-alert';
import { EmptyAlert } from './empty-alert';
import PTOTable from './Table';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { supervisory_10th_visit_alert } from 'src/config';
import { Permissions, usePermission } from 'src/rbac';
import PhysicianSignaturePending from './PhysicianSignaturePending';

const style = {
  position: 'absolute',
  top: '50%',
  borderRadius: '25px',
  outline: 'none',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50%',
  minWidth: '1100px',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
};

export const getDateInformation = (inputDate: string) => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const [year, month, date] = inputDate.split('T')[0].split('-').map(Number);
  const givenDate = new Date(year, month - 1, date);
  givenDate.setHours(0, 0, 0, 0);

  const timeDifference = givenDate.getTime() - currentDate.getTime();
  const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24));

  let result;

  if (daysDifference === 0) {
    result = 'Today';
  } else if (daysDifference === 1) {
    result = 'Tomorrow';
  } else if (daysDifference === -1) {
    result = 'Yesterday';
  } else if (daysDifference > 1) {
    result = `${daysDifference} days ahead`;
  } else if (daysDifference < -1) {
    result = `${Math.abs(daysDifference)} days ago`;
  }

  return result;
}

const DepartmentDashboard = () => {
  const { READ_MONTH_END_ALERT, CERTIFY_SIGN_MONTH_END_ALERT } = permissions;
  const [isMonthEnd, setIsMonthEnd] = useState<boolean | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const ptoUpdateResponse = useSelector((state: any) => state?.updateDepartmentPTORequestReuder);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [getCurrentItem, setGetCurrentItem] = React.useState<any>(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [isSupervisoryAlertEnabled] = useFeatureToggleWhenReady(supervisory_10th_visit_alert, 'ENTERPRISE');

  const dispatch = useDispatch();
  const dashboardDepartmentCountResponse = useSelector(
    (state: any) => state?.getDepartmentDashboardCountReducer?.response
  );
  const getPTOResponse = useSelector((state: any) => state?.getDepartmentPTOReducer?.response);
  const getPTOResponseStatus = useSelector((state: any) => state.getDepartmentPTOReducer);

  const getMonthEndAlertData = useSelector((state: any) => state?.getMonthEndAlertCountReducer);
  const getListofMonthEndAlertData = useSelector(
    (state: any) => state?.getListofMonthEndAlertReducer?.response?.content
  );
  const checkloading = useSelector((state: any) => state?.getListofMonthEndAlertReducer?.loading);
  const getCertificationStatusResponse = useSelector(
    (state: any) => state.getCertificationStatusReducer
  );
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  // S9/THIN-7841 (D-24): gate the physician-signature-pending card on a permission, not the 'dor'
  // role identity — granted to the director family (dor/ador/rdo) via the catalog role→perm seed.
  const canViewPhysicianSignaturePending = usePermission(Permissions.VIEW_PHYSICIAN_SIGNATURE_PENDING);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleClick = (event: any, item: any) => {
    setAnchorEl(event.currentTarget);
    setGetCurrentItem(item);
  };

  const handleCloseDialog = () => {
    setAnchorEl(null);
    setGetCurrentItem(null);
  };

  const openDialog = Boolean(anchorEl);

  const buildPayload = (timePeriod: string) => {
    return {
      source: 'DASHBOARD',
      param: {
        timePeriod,
      },
    };
  };

  useEffect(() => {
    dispatch(getMonthEndAlertCount(buildPayload('ALL')));
    dispatch(getMonthEndAlertCount(buildPayload('THIS_MONTH')));
    dispatch(getSupervisoryAlertSettingAction())
  }, [dispatch]);

  useEffect(() => {
    if (
      getMonthEndAlertData?.response?.completedErrorCount ===
      getMonthEndAlertData?.response?.totalErrorCount
    ) {
      setIsMonthEnd(true);
    } else {
      setIsMonthEnd(false);
    }
  }, [getMonthEndAlertData]);

  useEffect(() => {
    const today = new Date();
    const last30Days = new Date(today);
    last30Days.setDate(last30Days.getDate() - 30);
    const dates = {
      startDate: moment(last30Days).format('YYYY-MM-DD'),
      endDate: moment(today).format('YYYY-MM-DD'),
    };

    dispatch(
      getListOfMonthAlertRequest({
        param: {
          sortBy: 'dueDate',
          sortDirection: 'asc',
          searchString: '',
          page: 0,
          pageSize: 3,
          ...dates,
        },
        source: 'CLOSEOUT',
      })
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getDepartmentPto({
        status: '',
      })
    );
    dispatch(getDepartmentDashboardCount());
    dispatch(getDepartmentTherapyMin());
  }, [dispatch]);

  useEffect(() => {
    if (ptoUpdateResponse?.response === null && !ptoUpdateResponse?.isRequestApproved) return;
    else if (ptoUpdateResponse?.response !== null && !ptoUpdateResponse?.isRequestApproved) {
      setOpen(false);
      dispatch(resetDepartmentPTORequest());
      setResponseMessage(
        ptoUpdateResponse?.response?.message || ptoUpdateResponse?.response?.title
      );
      setTimeout(() => {
        setResponseMessage('');
      }, 3000);
      return;
    } else {
      setOpen(false);
      setResponseMessage(
        ptoUpdateResponse?.response?.message || ptoUpdateResponse?.response?.title
      );
      dispatch(
        getDepartmentPto({
          status: '',
        })
      );
      dispatch(resetDepartmentPTORequest());
      dispatch(getDepartmentDashboardCount());
      setTimeout(() => {
        setResponseMessage('');
      }, 3000);
      return;
    }
  }, [ptoUpdateResponse, dispatch]);

   

  const pendingAppointments = getPTOResponse?.filter(
    (appointment: any) => appointment?.ptoStatus === 'PENDING'
  );

  const DisplayMessage: React.FC<{ data: any }> = ({ data }) => {
    return (
      <>
        <Typography
          variant="body2"
          color={'text.secondary'}
        >
          {data.info}
        </Typography>
        {data.actionReq && <RedirectByActionAlert data={data} />}
      </>
    );
  };

  const isLastDayOfMonth = (date: Date) => {
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    return nextDay.getMonth() !== date.getMonth();
  };

  const handleNavigate = () => {
    const today = new Date();
    const last30Days = new Date(today);
    last30Days.setDate(last30Days.getDate() - 30);
    const dates = {
      startDate: moment(last30Days).format('YYYY-MM-DD'),
      endDate: moment(today).format('YYYY-MM-DD'),
    };
    navigate('/monthly-closeout-alerts/month-day-alert', { state: dates });
  };
  const isLessThan1140 = useMediaQuery('(max-width: 1439px)');

  return (
    <>
      <Stack
        gap="2rem"
        sx={{ width: '100%', height: '100%', padding: '2rem 2rem 2rem 4rem' }}
      >
        <Typography variant="h4">Department Compliance & Alerts</Typography>
        <Stack
          flexDirection={`${isLessThan1140 ? 'column' : 'row'}`}
          gap="2rem"
        >
          <Card
            sx={{
              minWidth: '484px',
              boxShadow:
                '0px 5px 22px 0px rgba(0, 0, 0, 0.06), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.06) !important',
            }}
          >
            <Stack
              sx={{ width: '90%', margin: 'auto', marginTop: '2rem' }}
              flexDirection="row"
              gap="1rem"
            >
              <Typography variant="h6">Today&apos;s tasks</Typography>

              <Stack
                sx={{
                  color: '#111927',
                  border: '1px solid #00000042',
                  borderRadius: '12px',
                  padding: '2px 10px',
                  fontWeight: '500',
                  fontSize: '12px',
                }}
              >
                {`${dashboardDepartmentCountResponse?.totalTaskCount || 0} tasks`}
              </Stack>
            </Stack>
            {dashboardDepartmentCountResponse?.totalTaskCount === 0 && (
              <Stack sx={{ marginTop: '4rem', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  alt=""
                  width="140px"
                  height="140px"
                  src={dashboardIcon}
                />
                <Typography
                  sx={{ color: '#11192761' }}
                  variant="subtitle1"
                >
                  There are no task today
                </Typography>
              </Stack>
            )}
            {dashboardDepartmentCountResponse?.totalTaskCount !== 0 && (
              <Stack sx={{ marginTop: '1rem' }}>
                <Stack
                  sx={{ padding: '1.1rem' }}
                  flexDirection="row"
                >
                  <Stack
                    flexDirection="row"
                    gap="1rem"
                    alignItems="center"
                    sx={{ width: '60%' }}
                  >
                    <img
                      width="24px"
                      height="24px"
                      src={clipboard}
                      alt="clipboard"
                    />
                    <Typography variant="body2">
                      {`${dashboardDepartmentCountResponse?.overDueTaskCount || 0}`} Overdue
                      sessions
                    </Typography>
                    <img
                      width="24px"
                      height="24px"
                      src={alertCicleIcon}
                      alt="alertCicleIcon"
                    />
                  </Stack>
                  <Stack
                    flexDirection="row"
                    gap="1rem"
                    alignItems="center"
                    sx={{ width: '40%', cursor: 'pointer', color: '#384250' }}
                    onClick={() => navigate('/scheduling/therapist')}
                  >
                    <img
                      width="24px"
                      height="24px"
                      src={calendarIcon}
                      alt="calendarIcon"
                    />
                    <Typography variant="buttonText">Assign</Typography>
                  </Stack>
                </Stack>
                <Divider />
                <Stack
                  sx={{ padding: '1.1rem' }}
                  flexDirection="row"
                >
                  <Stack
                    flexDirection="row"
                    gap="1rem"
                    alignItems="center"
                    sx={{ width: '60%' }}
                  >
                    <img
                      width="24px"
                      height="24px"
                      src={clipboard}
                      alt="clipboard"
                    />
                    <Typography variant="body2">
                      {dashboardDepartmentCountResponse?.unAssignTaskCount || 0} Unassigned sessions
                    </Typography>
                  </Stack>
                  <Stack
                    flexDirection="row"
                    gap="1rem"
                    alignItems="center"
                    sx={{ width: '40%', cursor: 'pointer', color: '#384250' }}
                    onClick={() => navigate('/scheduling/therapist')}
                  >
                    <img
                      width="24px"
                      height="24px"
                      src={calendarIcon}
                      alt="calendarIcon"
                    />
                    <Typography variant="buttonText">Assign</Typography>
                  </Stack>
                </Stack>
               
                <Divider />
                <Stack
                  sx={{ padding: '1.1rem' }}
                  flexDirection="row"
                >
                  <Stack
                    flexDirection="row"
                    gap="1rem"
                    alignItems="center"
                    sx={{ width: '60%' }}
                  >
                    <img
                      width="24px"
                      height="24px"
                      src={mailIcon}
                      alt="mailIcon"
                    />
                    <Typography variant="body2">
                      {dashboardDepartmentCountResponse?.ptoRequestCount || 0} PTO requests
                    </Typography>
                  </Stack>
                  <Stack
                    flexDirection="row"
                    gap="1rem"
                    alignItems="center"
                    sx={{ width: '40%', cursor: 'pointer', color: '#384250' }}
                    onClick={handleOpen}
                  >
                    <img
                      width="24px"
                      height="24px"
                      src={mailIcon1}
                      alt="mailIcon1"
                    />
                    <Typography variant="buttonText">Review</Typography>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Card>
          <Card sx={{ width: '100%' }}>
            <Stack
              sx={{ width: '90%', margin: 'auto', marginTop: '2rem' }}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap="1rem"
            >
              <Typography variant="h6">Month-End Alerts</Typography>
              <Button
                sx={{ color: '#111927', fontSize: '13px' }}
                endIcon={<ArrowForwardOutlinedIcon />}
                onClick={handleNavigate}
                disabled={!permissionData?.includes(READ_MONTH_END_ALERT)}
              >
                View All
              </Button>
            </Stack>
            {isMonthEnd && (
              <Stack
                flexDirection="row"
                sx={{ margin: '4rem' }}
                alignItems="center"
                justifyContent="center"
                gap="4rem"
              >
                <img
                  width="164px"
                  height="150px"
                  src={monthAlertIcon}
                  alt=""
                />
                <Stack
                  gap="2rem"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant="h5">You&apos;ve completed all your alerts!</Typography>
                  {permissionData?.includes(CERTIFY_SIGN_MONTH_END_ALERT) &&
                    isLastDayOfMonth(new Date()) &&
                    !getCertificationStatusResponse.response?.data && (
                      <Stack
                        sx={{
                          border: '1px solid #38425080',
                          borderRadius: '12px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 'max-content',
                          padding: '8px 22px',
                          gap: '1rem',
                          cursor: 'pointer',
                        }}
                        flexDirection="row"
                      >
                        <img
                          width="24px"
                          alt=""
                          height="24px"
                          src={monthEndPencil}
                        />
                        <Typography>Sign a certification statement</Typography>
                      </Stack>
                    )}
                </Stack>
              </Stack>
            )}
            {isMonthEnd === false && !getMonthEndAlertData?.loading ? (
              <Stack
                flexDirection="row"
                gap="2rem"
                sx={{ margin: '1.5rem' }}
              >
                <Card
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '80%',
                    height: '244px',
                    paddingTop: '16px',
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      position: 'absolute',
                      top: '10%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    Open Issues This Month
                  </Typography>

                  <Stack
                    gap="1rem"
                    pl={4}
                    mt={6}
                  >
                    <Grid>
                      <Grid
                        p={2}
                        sx={{
                          backgroundColor: '#F044381F',
                          borderRadius: '20px',
                          margin: 'auto',
                          width: '103px',
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h7">Errors</Typography>
                        <SvgIcon
                          fontSize="large"
                          color="error"
                        >
                          <CancelRoundedIcon />
                        </SvgIcon>
                      </Grid>
                      <Typography
                        variant="h5"
                        mt={2}
                      >
                        {getMonthEndAlertData?.thismonth?.totalErrorCount}
                        <Typography variant="caption">
                          /{getMonthEndAlertData?.response?.totalErrorCount} all months
                        </Typography>
                      </Typography>
                    </Grid>
                  </Stack>

                  <Stack
                    gap="1rem"
                    pr={4}
                    mt={6}
                  >
                    <Grid>
                      <Grid
                        p={2}
                        sx={{
                          backgroundColor: '#F044381F',
                          borderRadius: '20px',
                          margin: 'auto',
                          width: '103px',
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h7">Warnings</Typography>
                        <SvgIcon
                          fontSize="large"
                          color="warning"
                        >
                          <ReportProblemRoundedIcon />
                        </SvgIcon>
                      </Grid>
                      <Typography
                        variant="h5"
                        mt={2}
                      >
                        {getMonthEndAlertData?.thismonth?.totalWarningCount}
                        <Typography variant="caption">
                          /{getMonthEndAlertData?.response?.totalWarningCount} all months
                        </Typography>
                      </Typography>
                    </Grid>
                  </Stack>
                </Card>
                {checkloading ? (
                  <Stack sx={{ width: '100%' }}>
                    {Array.from({ length: 9 }).map((_, index) => {
                      return <Skeleton key={index} />;
                    })}
                  </Stack>
                ) : !getListofMonthEndAlertData?.length ? (
                  <>
                    <Stack
                      flexDirection="column"
                      justifyContent={'center'}
                      alignItems={'center'}
                      sx={{ width: '100%' }}
                    >
                      <Stack>
                        <EmptyAlert />
                      </Stack>
                      <Stack mt={2}>
                        <Typography
                          variant="subtitle1"
                          color={'#6C737F'}
                        >
                          There are no alerts at the moment
                        </Typography>
                      </Stack>
                    </Stack>
                  </>
                ) : (
                  <Stack sx={{ width: '100%' }}>
                    <Stack
                      sx={{ width: '94%', margin: '0 auto' }}
                      flexDirection="row"
                    >
                      <Typography
                        sx={{ width: '60%' }}
                        variant="label"
                      >
                        Top Alerts
                      </Typography>
                      <Typography
                        sx={{ width: '40%' }}
                        variant="label"
                      >
                        Days Delay
                      </Typography>
                    </Stack>
                    {!!getListofMonthEndAlertData?.length &&
                      getListofMonthEndAlertData?.map((alertData: any) => (
                        <React.Fragment key={alertData?.uuid}>
                          <Stack
                            flexDirection="row"
                            sx={{
                              width: '94%',
                              marginTop: '1rem',
                              margin: '1rem auto 1rem auto',
                            }}
                          >
                            <Stack
                              flexDirection="row"
                              gap="1rem"
                              sx={{ width: '60%' }}
                              alignItems="center"
                            >
                              <Stack
                                sx={{
                                  backgroundColor: '#F044381F',
                                  padding: '8px',
                                  height: 'max-content',
                                  borderRadius: '8px',
                                }}
                              >
                                {alertData?.type === 'ERROR' && <ErrorIconAlert />}
                                {alertData?.type === 'WARNING' && <WarningIconAlert />}
                              </Stack>
                              <Stack>
                                <Typography
                                  sx={{ color: '#384250' }}
                                  variant="captionText"
                                >
                                  {alertData?.alertMessage}
                                </Typography>
                              </Stack>
                            </Stack>
                            <Stack
                              sx={{ width: '40%' }}
                              flexDirection="row"
                              justifyContent="space-between"
                            >
                              <Stack>
                                <Typography
                                  sx={{ color: '#384250' }}
                                  variant="captionText"
                                >
                                  {getDateInformation(alertData?.dueDate)}
                                </Typography>
                                <Typography variant="label">{alertData?.ownerName}</Typography>
                              </Stack>

                              {alertData.actionReq ? (
                                <Grid
                                  onMouseEnter={(event: any) => handleClick(event, alertData)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <LinkIcon />
                                </Grid>
                              ) : (
                                <Grid
                                  onMouseEnter={(event: any) => handleClick(event, alertData)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <SquareIcon />
                                </Grid>
                              )}

                              {openDialog && getCurrentItem?.uuid === alertData.uuid && (
                                <ClickAwayListener onClickAway={handleCloseDialog}>
                                  <Box
                                    sx={{
                                      borderRadius: '12px',
                                      background: '#FFF',
                                      boxShadow: '0px 3px 14px 0px rgba(0, 0, 0, 0.08)',
                                      width: '268px',
                                      position: 'absolute',
                                      right: '0px',
                                      zIndex: 999,
                                      padding: '16px',
                                      textAlign: 'left',
                                    }}
                                    onMouseLeave={handleCloseDialog}
                                  >
                                    {<DisplayMessage data={alertData} />}
                                  </Box>
                                </ClickAwayListener>
                              )}
                            </Stack>
                          </Stack>
                          <Divider />
                        </React.Fragment>
                      ))}
                  </Stack>
                )}
              </Stack>
            ) : (
              Array.from(Array(10).keys()).map((index: number) => {
                return <Skeleton key={index} />;
              })
            )}
          </Card>
        </Stack>
         {(isSupervisoryAlertEnabled || canViewPhysicianSignaturePending) && (
          <Stack
            flexDirection={`${isLessThan1140 ? 'column' : 'row'}`}
            alignItems="stretch"
            gap="2rem"
          >
            {isSupervisoryAlertEnabled && (
              <Card
                sx={{
                  flex: 2,
                  minWidth: 0,
                  overflow: 'auto',
                  padding: '1rem',
                  boxShadow:
                    '0px 5px 22px 0px rgba(0, 0, 0, 0.06), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.06) !important',
                }}
              >
                <TenthSupervisorAlert />
              </Card>
          )}

          {canViewPhysicianSignaturePending && (
            <Card
              sx={{
                flex: 3,
                minWidth: 0,
                height: 720,
                display: 'flex',
                flexDirection: 'column',
                padding: '1rem',
                boxShadow:
                  '0px 5px 22px 0px rgba(0, 0, 0, 0.06), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.06) !important',
              }}
            >
              <PhysicianSignaturePending />
            </Card>
          )}
        </Stack>
      )}

      </Stack>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', padding: '2rem 0 0 2rem' }}>
              <Typography
                id="modal-modal-title"
                variant="h6"
                component="h2"
              >
                PTO Requests
              </Typography>
              {pendingAppointments?.length ? (
                <Chip
                  label={`${pendingAppointments?.length} Requests`}
                  variant="outlined"
                  sx={{ marginLeft: '10px' }}
                />
              ) : null}
            </Box>

            <IconButton
              sx={{ padding: '2rem 2rem 0 2rem' }}
              onClick={handleClose}
            >
              <CloseOutlinedIcon />
            </IconButton>
          </Stack>

          <Stack
            sx={{ margin: '0 auto 1rem auto', width: '98%', overflow: 'auto', maxHeight: '65vh' }}
          >
            {getPTOResponseStatus?.loading ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '45vh',
                  }}
                >
                  <img
                    src={LoaderIcon}
                    alt="loader.."
                  />
                </div>
              </>
            ) : (
              <PTOTable getPTOResponse={pendingAppointments} />
            )}
          </Stack>
        </Box>
      </Modal>
      {responseMessage !== '' && (
        <SimpleSnackbar
          message={responseMessage}
          isError={ptoUpdateResponse?.isRequestApproved}
        />
      )}
    </>
  );
};

export default DepartmentDashboard;

const SquareIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M10 13.3333V10M10 6.66667H10.0083M6.5 17.5H13.5C14.9001 17.5 15.6002 17.5 16.135 17.2275C16.6054 16.9878 16.9878 16.6054 17.2275 16.135C17.5 15.6002 17.5 14.9001 17.5 13.5V6.5C17.5 5.09987 17.5 4.3998 17.2275 3.86502C16.9878 3.39462 16.6054 3.01217 16.135 2.77248C15.6002 2.5 14.9001 2.5 13.5 2.5H6.5C5.09987 2.5 4.3998 2.5 3.86502 2.77248C3.39462 3.01217 3.01217 3.39462 2.77248 3.86502C2.5 4.3998 2.5 5.09987 2.5 6.5V13.5C2.5 14.9001 2.5 15.6002 2.77248 16.135C3.01217 16.6054 3.39462 16.9878 3.86502 17.2275C4.3998 17.5 5.09987 17.5 6.5 17.5Z"
        stroke="#6C737F"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const LinkIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.00016 10C5.00016 9.53976 5.37326 9.16667 5.8335 9.16667L14.1668 9.16667C14.6271 9.16667 15.0002 9.53976 15.0002 10C15.0002 10.4602 14.6271 10.8333 14.1668 10.8333L5.8335 10.8333C5.37326 10.8333 5.00016 10.4602 5.00016 10Z"
        fill="#6C737F"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.8335 6.66667C3.99255 6.66667 2.50016 8.15905 2.50016 10C2.50016 11.8409 3.99255 13.3333 5.8335 13.3333H7.50016C7.9604 13.3333 8.3335 13.7064 8.3335 14.1667C8.3335 14.6269 7.9604 15 7.50016 15H5.8335C3.07207 15 0.833496 12.7614 0.833496 10C0.833496 7.23858 3.07207 5 5.8335 5H7.50016C7.9604 5 8.3335 5.3731 8.3335 5.83333C8.3335 6.29357 7.9604 6.66667 7.50016 6.66667H5.8335Z"
        fill="#6C737F"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.6668 5.83333C11.6668 5.3731 12.0399 5 12.5002 5H14.1668C16.9283 5 19.1668 7.23858 19.1668 10C19.1668 12.7614 16.9283 15 14.1668 15H12.5002C12.0399 15 11.6668 14.6269 11.6668 14.1667C11.6668 13.7064 12.0399 13.3333 12.5002 13.3333H14.1668C16.0078 13.3333 17.5002 11.8409 17.5002 10C17.5002 8.15905 16.0078 6.66667 14.1668 6.66667H12.5002C12.0399 6.66667 11.6668 6.29357 11.6668 5.83333Z"
        fill="#6C737F"
      />
    </svg>
  );
};